/**
 * useQuickActions — 플로팅 스피드다이얼 액션 레지스트리 훅.
 *
 * 【역할】
 * - 우하단 floating action button 에 표시될 빠른 액션 목록을 전역 store 에 등록/해제.
 * - 페이지별 context 로 필터링하여 "현재 화면에 의미 있는 액션" 만 노출.
 *
 * 【주요 기능】
 * - register(action): 동일 key 있으면 교체
 * - unregister(key): 개별 해제
 * - clear(context?): 전체 또는 특정 context 만 초기화
 * - getByContext(context?): context 매칭 또는 context 없는 전역 액션 조회
 *
 * 【내부 상태】
 * - Zustand store actions: QuickAction[].
 * - 페이지 마운트 시 register, unmount 시 unregister 패턴 권장 (useEffect 반환 함수).
 *
 * 【사용 예시】
 * ```tsx
 * const { register, unregister } = useQuickActions()
 * useEffect(() => {
 *   register({ key: 'create-user', label: '신규 사용자', icon: <UserAddOutlined />, onClick: openModal, context: 'user-list' })
 *   return () => unregister('create-user')
 * }, [])
 * ```
 *
 * 【왜 이 패턴?】
 * - SpeedDial 렌더링 컴포넌트(Layout 내부) 와 액션 제공 페이지를 decoupling.
 * - props drilling 없이 모든 페이지가 자유롭게 액션을 등록.
 */
import { useCallback } from 'react'
import { create } from 'zustand'
import type { ReactNode } from 'react'

/** 스피드다이얼 액션 1건 — context 로 페이지 범위 제한 가능. */
export interface QuickAction {
  key: string
  label: string
  icon?: ReactNode
  onClick: () => void
  context?: string // 특정 페이지 범위
}

/** Zustand store 타입 — 배열 기반 FIFO 레지스트리. */
interface QuickActionsState {
  actions: QuickAction[]
  register: (action: QuickAction) => void
  unregister: (key: string) => void
  clear: (context?: string) => void
}

const useQuickActionsStore = create<QuickActionsState>((set) => ({
  actions: [],
  register: (action) =>
    set((s) => ({
      actions: [...s.actions.filter((a) => a.key !== action.key), action]
    })),
  unregister: (key) => set((s) => ({ actions: s.actions.filter((a) => a.key !== key) })),
  clear: (context) =>
    set((s) => ({
      actions: context ? s.actions.filter((a) => a.context !== context) : []
    }))
}))

/**
 * useQuickActions — 액션 배열 + 등록/해제 API 반환.
 */
export function useQuickActions() {
  const actions = useQuickActionsStore((s) => s.actions)
  const register = useQuickActionsStore((s) => s.register)
  const unregister = useQuickActionsStore((s) => s.unregister)
  const clear = useQuickActionsStore((s) => s.clear)

  /**
   * context 필터링된 액션 목록 반환.
   * @param context - 지정 시 일치하거나 context 없는 전역 액션만, 미지정 시 전체.
   */
  const getByContext = useCallback(
    (context?: string) =>
      context ? actions.filter((a) => !a.context || a.context === context) : actions,
    [actions]
  )

  return { actions, register, unregister, clear, getByContext }
}
